import { ReactNode } from 'react';

import { ButtonV1 } from '../buttons';

import { ButtonAlt } from '../ButtonAlt';

/**
 * Configuration for a button action.
 */
interface IButtonAction {
  /** The text to display on the button. */
  label: string;
  /** Callback function triggered when the button is clicked. */
  onClick: () => void;
}

/**
 * Data structure for a navigation link CTA item.
 */
interface INavLinkCTAItem {
  /** Accessible title for the link. */
  ariaTitle: string;
  /** The destination URL. */
  href: string;
  /** Whether the link should have an inverted style. */
  isInverted?: boolean;
  /** The text to display for the link. */
  label: string;
}

interface IFeedbackSectionViewProps {
  /**
   * Provides additional context to the user about the section title
   */
  description: string;
  /**
   * Renders an SVG image or icon as the main visual element of the section.
   */
  illustration: ReactNode;
  /**
   * it will take label and onClick handler to show in main action button
   */
  mainCTA?: IButtonAction;
  /**
   * This renders a CTA with as a navigation link button.
   */
  navLinkCTA?: INavLinkCTAItem;
  /**
   * it is same as mainCTA but it is optional to show in secondary button
   */
  otherCTA?: IButtonAction;
  /**
   * The main heading of the section.
   */
  title: string;
}

/**
 * -----------------------------------------------------------------------------
 * The following component will render a screen which is as follows
 * It will have a logo at left top corner
 * It will contain a illustration center of the screen
 * It will have heading, description and button below the illustration
 * It can have single or two buttons
 */
export function FeedbackSectionView({
  description,
  illustration,
  mainCTA,
  navLinkCTA,
  otherCTA,
  title,
}: IFeedbackSectionViewProps) {
  return (
    <div className="relative flex h-full items-center justify-center lg:h-screen">
      <div className="max-w-xl p-4 text-center">
        <div>{illustration}</div>
        <div className="pt-10 text-2xl font-bold leading-tight text-green-800 sm:text-3xl">
          {title}
        </div>
        <p className="pt-4 text-sm text-green-950">{description}</p>
        <div className="pt-8 sm:pt-10">
          {otherCTA ? (
            <div className="inline-flex flex-col gap-4 sm:flex-row">
              <ButtonV1
                className="from-btn-secondary"
                label={otherCTA.label}
                onClick={otherCTA.onClick}
                type="button"
              />
            </div>
          ) : null}

          {mainCTA ? (
            <ButtonV1
              label={mainCTA.label}
              onClick={mainCTA.onClick}
              type="button"
            />
          ) : null}

          {navLinkCTA ? (
            <ButtonAlt
              href={navLinkCTA.href}
              invert={navLinkCTA.isInverted}
              title={navLinkCTA.ariaTitle}
            >
              {navLinkCTA.label}
            </ButtonAlt>
          ) : null}
        </div>
      </div>
    </div>
  );
}
